import { t } from "@lingui/macro";
import { differenceInHours, parse } from "date-fns";
import { isEqual, partition, sortBy, uniq } from "lodash";

import {
  isAccessibleBookingSlotAvailability,
  isBlockedByBlockOrEventSlotAvailability,
  isBlockedOrUnavailableSlotAvailability,
  isOpenSlotAvailability,
} from "@/utils/slotAvailability";
import { WeatherConditionProps } from "@whoosh/design-system/src/components/display/WeatherCondition";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { normalizeBookingRule } from "@whoosh/features/src/Restrictions";
import { restrictionUtils } from "@whoosh/utilities";
import {
  compareAgendaTimes,
  differenceInAgendaTimeMinutes,
} from "@whoosh/utilities/src/dates";

import { TimeListEventsCard } from "../../../../Events/components/TimeListEventsCard/TimeListEventsCard";
import { TimeListEventsCard_EventSession$key } from "../../../../Events/components/TimeListEventsCard/__generated__/TimeListEventsCard_EventSession.graphql";
import type { TeeSheetFilterType } from "../../../types";
import { CombinedTimeSlotAndRequestRow } from "../components/CombinedTimeSlotAndRequestRow";
import {
  GroupedRequestRowProps,
  GroupedRequestTimeSlotRow,
} from "../components/GroupedRestrictionRequest/GroupedRequestTimeSlotRow";
import {
  RestrictionBlock,
  RestrictionBlockProps,
} from "../components/RestrictionBlock";
import { RestrictionInfoBar } from "../components/RestrictionInfoBar";
import { RestrictionInfoBarProps } from "../components/RestrictionInfoBar";
import {
  RequestTimeSlotRowProps,
  RequestTimeSlotRow,
} from "../components/RestrictionRequest/RequestTimeSlotRow";
import {
  TimeSlotListBlock,
  TimeSlotListBlockProps,
} from "../components/TimeSlotListBlock";
import { TimeSlotListWeatherRow } from "../components/TimeSlotListWeatherRow";
import { TimeSlotRow, TimeSlotRowProps } from "../components/TimeSlotRow";
import {
  WaitlistCta,
  WaitlistCtaProps,
} from "../components/WaitlistCta/WaitlistCta";
import {
  HoleCount,
  TimeSlotListInternalQuery$data,
} from "../components/__generated__/TimeSlotListInternalQuery.graphql";
import { getArrayOfWeatherConditions } from "./getArrayOfWeatherConditions";

const MIN_RENDERED_AGENDA_ROWS_BETWEEN_WAITLIST_CTAS = 21;

type TimeSlot = {
  courseName: string;
  data: TimeSlotRowProps;
  id: string;
  time: string;
};

type restrictionCourse = {
  id: string;
  name: string;
};

type RequestRestrictionState =
  | "blocking"
  | "bookable"
  | "published"
  | "request_open";

type RequestRestrictionNode = {
  id: string;
  requestConfig?: { id: string } | null;
  startAt?: string | null;
  endAt?: string | null;
  name?: string;
  displayName?: string | null;
  memberMessage?: string | null;
  allDay?: boolean;
  visibility?:
    | "NONE"
    | "RESTRICTION_ONLY"
    | "SLOTS_AND_RESTRICTION"
    | "SLOTS_AND_RESTRICTION_INLINE"
    | "SLOTS_ONLY"
    | null;
  visibilityColor?: string | null;
};

type RequestRestrictionEdge = {
  liftAt?: string | null;
  requestOpens?: string | null;
  requestCloses?: string | null;
  node?: RequestRestrictionNode | null;
};

const getRestrictionColor = (
  visibilityColor?: string | null
): RestrictionBlockProps["visibilityColor"] => {
  switch (visibilityColor) {
    case "A":
    case "B":
    case "C":
    case "D":
    case "E":
    case "F":
      return visibilityColor;
    default:
      return null;
  }
};

type EventProps = {
  id?: string;
  name?: string;
  startAt: string | null;
  endAt?: string | null;
  data: TimeListEventsCard_EventSession$key | null;
};

const restrictionCourseMessage = (
  courseIds: string[],
  restrictionCourses: restrictionCourse[]
) => {
  let courseMessage: string | undefined = undefined;
  const filteredCourses = restrictionCourses.filter((e) =>
    courseIds.includes(e.id)
  );
  const isAll = restrictionCourses.length === 0;

  if (courseIds.length > 1) {
    if (courseIds.length === filteredCourses.length || isAll) {
      courseMessage = t`All Courses`;
    } else {
      courseMessage = `${filteredCourses.map((e) => e.name).join(", ")}`;
    }
  }
  return courseMessage;
};

const getRestrictionTimes = (
  startAt?: string | null,
  endAt?: string | null
) => {
  const { startAt: restrictionStartAt, endAt: restrictionEndAt } =
    restrictionUtils.getTimeOperatorAndStartEndAt(startAt, endAt);
  const hasStartAt = !!startAt;

  return {
    startAt: restrictionStartAt ?? restrictionEndAt ?? null,
    endAt: restrictionEndAt ?? null,
    displayStartAt: hasStartAt ? restrictionStartAt ?? null : null,
  };
};

const getRestrictionDisplayTiming = (
  allDay?: boolean | null,
  startAt?: string | null,
  endAt?: string | null
) => {
  const hasExplicitTimeRange = !!startAt || !!endAt;
  const resolvedAllDay = hasExplicitTimeRange ? !!allDay : true;

  return {
    allDay: resolvedAllDay,
    ...getRestrictionTimes(startAt, endAt),
  };
};

const checkIfEitherArrayIncludesAMatch = (
  ar1: (string | undefined)[],
  ar2: (string | undefined)[]
) => {
  return ar1.reduce((acc, cur) => {
    if (cur && ar2.includes(cur)) {
      return true;
    }
    return acc;
  }, false);
};

const getRequestRestrictionState = (
  restrictionEdge: RequestRestrictionEdge | null | undefined,
  nowMs: number
): RequestRestrictionState => {
  if (restrictionEdge?.liftAt) {
    return "published";
  }

  const requestOpensMs = restrictionEdge?.requestOpens
    ? Date.parse(restrictionEdge.requestOpens)
    : null;
  const requestClosesMs = restrictionEdge?.requestCloses
    ? Date.parse(restrictionEdge.requestCloses)
    : null;
  const hasOpened =
    requestOpensMs === null ||
    Number.isNaN(requestOpensMs) ||
    requestOpensMs <= nowMs;
  const hasClosed =
    requestClosesMs !== null &&
    !Number.isNaN(requestClosesMs) &&
    requestClosesMs < nowMs;

  const isDefaultRequestRule = !restrictionEdge?.node?.requestConfig;

  if (!hasOpened) {
    return "blocking";
  }

  if (!hasClosed) {
    return "request_open";
  }

  return isDefaultRequestRule ? "bookable" : "blocking";
};

const getAgendaRowSortStartAt = (
  row: TeeSheetAgendaRowData,
  firstTimeSlot: string
) => {
  if (
    (row.type === "BlockingRestriction" || row.type === "InfoRestriction") &&
    row.data.allDay
  ) {
    return firstTimeSlot;
  }

  return row.startAt;
};

const getTimeFromDateTime = (dateTime?: string | null) =>
  dateTime?.match(/\d{2}:\d{2}:\d{2}/)?.[0] ?? null;

const isWithinTeeSheetTimeRange = (
  time: string,
  timeGTE?: string,
  timeLTE?: string
) => {
  if (!timeGTE && !timeLTE) {
    return true;
  }

  if (!time) {
    return false;
  }

  if (timeGTE && timeLTE && timeGTE > timeLTE) {
    return time >= timeGTE || time <= timeLTE;
  }

  if (timeGTE && time < timeGTE) {
    return false;
  }

  if (timeLTE && time > timeLTE) {
    return false;
  }

  return true;
};

export const getTeeSheetComponents = (
  data: TimeSlotListInternalQuery$data,
  onSlotPress: (slotId: string) => void,
  viewFilter: TeeSheetFilterType,
  courseIds: string[],
  holeCount: HoleCount | null,
  onRequestSlotPress: (id: string, timeString: string) => void,
  onJoinableRowPress: (slotId: string) => void,
  groupSize: number,
  currentSlotId?: string | null,
  shouldShowCourseName?: boolean,
  teeSheetTimes?: { timeGTE?: string; timeLTE?: string },
  clientCanFulfillRequests?: boolean,
  supportsPublic?: boolean,
  hideUnavailableTimes?: boolean,
  serverTimeOffsetMs?: number | null,
  requestConfigEnabled = false,
  facilityPermissions?: TimeSlotRowProps["facilityPermissions"]
) => {
  const agenda = data.node?.agendas?.edges
    ? data.node.agendas.edges[0]?.node
    : null;
  const nowMs = Date.now();

  // GRAB ALL NECESSARY PROPS
  const queueSlotId = agenda?.queueSlot.id || null;
  // Weather Conditions
  const weatherConditions = getArrayOfWeatherConditions(data);

  const isMultiCourse = data.node?.courses && data.node.courses.count > 1;

  const clubVisibility =
    data.session?.currentClientProfile?.member?.club?.visibility ?? "PUBLIC";
  const firstTimeSlot =
    getTimeFromDateTime(agenda?.timeSlots?.firstDateTime) ??
    agenda?.timeSlots?.edges?.find((edge) => edge?.node?.time)?.node?.time ??
    "00:00:00";

  type TeeSheetBlockNode = {
    id?: string | null;
    startAt?: string | null;
    durationMins?: number | null;
    blockSeries?: {
      isVisibleToMembers?: boolean | null;
      name?: string | null;
      color?: TimeSlotListBlockProps["color"] | null;
    } | null;
    courses?: {
      edges?: ReadonlyArray<
        | {
            node?: {
              id?: string | null;
            } | null;
          }
        | null
        | undefined
      > | null;
    } | null;
  };

  const blockMatchesSelectedCourses = (block?: TeeSheetBlockNode | null) => {
    const blockCourseIds =
      block?.courses?.edges?.flatMap((courseEdge) =>
        courseEdge?.node?.id ? [courseEdge.node.id] : []
      ) ?? [];

    return checkIfEitherArrayIncludesAMatch(blockCourseIds, courseIds);
  };

  const buildBlockProps = (
    block?: TeeSheetBlockNode | null
  ): TimeSlotListBlockProps | null => {
    if (
      !block?.blockSeries?.name ||
      !block.blockSeries.color ||
      !blockMatchesSelectedCourses(block)
    ) {
      return null;
    }

    return {
      color: block.blockSeries.color,
      name: block.blockSeries.name,
      durationMins: block.durationMins ?? 0,
      startAt: block.startAt ?? null,
    };
  };

  const getBlockPropsKey = (block: TimeSlotListBlockProps) =>
    `${block.name}-${block.startAt ?? ""}-${block.durationMins}`;

  // Blocks
  const agendaBlockIds = new Set<string>();
  const agendaBlockKeys = new Set<string>();
  const inlineBlockIds = new Set<string>();
  const inlineBlockKeys = new Set<string>();

  const blocks: TimeSlotListBlockProps[] = agenda?.blocks?.edges
    ? agenda?.blocks?.edges?.reduce((acc: TimeSlotListBlockProps[], cur) => {
        const blockProps = buildBlockProps(cur?.node);

        if (blockProps) {
          if (cur?.node?.id) {
            agendaBlockIds.add(cur.node.id);
          }

          agendaBlockKeys.add(getBlockPropsKey(blockProps));
          acc.push(blockProps);
        }

        return acc;
      }, [])
    : [];

  // Events
  const events: EventProps[] = agenda?.events?.edges
    ? agenda.events.edges.reduce((acc: EventProps[], cur) => {
        const matchingSessions = cur?.node?.eventSessions?.edges
          ? cur.node.eventSessions.edges.filter(
              (e) => e?.node?.agenda.date && e.node.agenda.date === agenda?.date
            )
          : undefined;
        if (matchingSessions?.length) {
          const newEvents = matchingSessions.reduce(
            (eventSessionAcc: EventProps[], eventSession) => {
              if (cur?.node && eventSession?.node) {
                eventSessionAcc.push({
                  id: cur.node.id,
                  name: cur.node.eventSeries.name,
                  startAt: eventSession.node?.startAt ?? "",
                  endAt: eventSession.node?.endAt ?? "",
                  data: eventSession.node,
                });
              }
              return eventSessionAcc;
            },
            []
          );
          acc.push(...newEvents);
        }

        return acc;
      }, [])
    : [];

  const timeSlotEdges = agenda?.timeSlots?.edges ?? [];

  // Restrictions
  const blockingRestrictions: RestrictionBlockProps[] = agenda
    ?.blockingRestrictions?.edges
    ? agenda.blockingRestrictions.edges.reduce(
        (acc: RestrictionBlockProps[], cur) => {
          const normalizedRule = normalizeBookingRule(cur?.node);

          if (
            (normalizedRule?.courses?.edges &&
              normalizedRule.courses.edges.length === 0) ||
            (normalizedRule?.courses?.edges &&
              checkIfEitherArrayIncludesAMatch(
                normalizedRule.courses.edges.flatMap((e) =>
                  e?.node?.id ? [e.node.id] : []
                ),
                courseIds
              ))
          ) {
            //Only show the restriction if the visibility is SLOTS_AND_RESTRICTION or RESTRICTION_ONLY
            if (
              ["SLOTS_AND_RESTRICTION", "RESTRICTION_ONLY"].includes(
                normalizedRule?.visibility ?? ""
              )
            ) {
              const { allDay, startAt, endAt, displayStartAt } =
                getRestrictionDisplayTiming(
                  normalizedRule?.allDay,
                  normalizedRule?.startAt,
                  normalizedRule?.endAt
                );
              const props: RestrictionBlockProps = {
                id: normalizedRule.id,
                name: normalizedRule.name,
                displayName: normalizedRule.displayName,
                liftAt: cur?.liftAt ?? null,
                memberMessage: normalizedRule.memberMessage,
                allDay,
                visibility: normalizedRule.visibility,
                visibilityColor: getRestrictionColor(
                  normalizedRule.visibilityColor
                ),
                courseMessage: restrictionCourseMessage(
                  courseIds,
                  normalizedRule?.courses?.edges?.flatMap((e) => {
                    return e?.node?.id
                      ? [{ id: e.node.id, name: e.node.name ?? "" }]
                      : [];
                  })
                ),
                startAt,
                endAt,
                displayStartAt,
              };
              acc.push(props);
            }
          }
          return acc;
        },
        []
      )
    : [];

  const closedRequestRestrictions: RestrictionBlockProps[] =
    requestConfigEnabled
      ? agenda?.configuredRequestRestrictions?.edges?.reduce(
          (acc: RestrictionBlockProps[], cur) => {
            const normalizedRule = normalizeBookingRule(cur?.node);
            const requestState = getRequestRestrictionState(cur, nowMs);

            if (
              !normalizedRule ||
              requestState !== "blocking" ||
              !["SLOTS_AND_RESTRICTION", "RESTRICTION_ONLY"].includes(
                normalizedRule.visibility ?? ""
              )
            ) {
              return acc;
            }

            if (
              (normalizedRule.courses?.edges &&
                normalizedRule.courses.edges.length === 0) ||
              (normalizedRule.courses?.edges &&
                checkIfEitherArrayIncludesAMatch(
                  normalizedRule.courses.edges.flatMap((e) =>
                    e?.node?.id ? [e.node.id] : []
                  ),
                  courseIds
                ))
            ) {
              const { startAt, endAt, displayStartAt } = getRestrictionTimes(
                normalizedRule.startAt,
                normalizedRule.endAt
              );

              acc.push({
                id: normalizedRule.id,
                name: normalizedRule.name,
                displayName: normalizedRule.displayName,
                liftAt: null,
                memberMessage: normalizedRule.memberMessage,
                allDay: normalizedRule.allDay,
                visibility: normalizedRule.visibility,
                visibilityColor: getRestrictionColor(
                  normalizedRule.visibilityColor
                ),
                courseMessage: restrictionCourseMessage(
                  courseIds,
                  normalizedRule.courses?.edges?.flatMap((e) => {
                    return e?.node?.id
                      ? [{ id: e.node.id, name: e.node.name ?? "" }]
                      : [];
                  })
                ),
                startAt,
                endAt,
                displayStartAt,
              });
            }

            return acc;
          },
          []
        ) ?? []
      : [];

  const infoRestrictions: RestrictionInfoBarProps[] = agenda?.infoRestrictions
    ?.edges
    ? agenda.infoRestrictions.edges.reduce(
        (acc: RestrictionInfoBarProps[], cur) => {
          const normalizedRule = normalizeBookingRule(cur?.node);

          if (
            (normalizedRule?.courses?.edges &&
              normalizedRule.courses.edges.length === 0) ||
            (normalizedRule?.courses?.edges &&
              checkIfEitherArrayIncludesAMatch(
                normalizedRule.courses.edges.flatMap((e) =>
                  e?.node?.id ? [e.node.id] : []
                ),
                courseIds
              ))
          ) {
            const { allDay, startAt, endAt, displayStartAt } =
              getRestrictionDisplayTiming(
                normalizedRule?.allDay,
                normalizedRule?.startAt,
                normalizedRule?.endAt
              );
            const props: RestrictionInfoBarProps = {
              id: normalizedRule.id,
              name: normalizedRule.name,
              displayName: normalizedRule.displayName,
              memberMessage: normalizedRule.memberMessage,
              allDay,
              visibilityColor: getRestrictionColor(
                normalizedRule.visibilityColor
              ),
              courseMessage: restrictionCourseMessage(
                courseIds,
                normalizedRule?.courses?.edges?.flatMap((e) => {
                  return e?.node?.id
                    ? [{ id: e.node.id, name: e.node.name ?? "" }]
                    : [];
                })
              ),
              startAt,
              endAt,
              displayStartAt,
            };
            acc.push(props);
          }
          return acc;
        },
        []
      )
    : [];

  const timedInfoRestrictionStartTimes = uniq(
    infoRestrictions.flatMap((restriction) =>
      !restriction.allDay && restriction.startAt ? [restriction.startAt] : []
    )
  );

  type RequestRestrictionSlotRule = NonNullable<
    ReturnType<typeof normalizeBookingRule>
  > & {
    liftAt?: string | null;
    requestOpens?: string | null;
    requestCloses?: string | null;
  };

  type RequestRestrictionSlotNode =
    | {
        type: "RequestSlot";
        id: string;
        time: string;
        availability: RequestTimeSlotRowProps["availability"];
        course: RequestTimeSlotRowProps["course"];
        restriction: RequestRestrictionSlotRule;
      }
    | {
        type: "Block";
        id: string;
        time: string;
        block: TimeSlotListBlockProps;
        restriction: RequestRestrictionSlotRule;
      };

  type TimeSlotWithRestrictionData = {
    id: string;
    time: string;
    availability: RequestTimeSlotRowProps["availability"];
    course: RequestTimeSlotRowProps["course"];
    eventSlot?: { type?: string | null } | null;
    block?: TeeSheetBlockNode | null;
    requestRestrictions?: {
      edges?: ReadonlyArray<RequestRestrictionEdge | null | undefined> | null;
    } | null;
    configuredRequestRestrictions?: {
      edges?: ReadonlyArray<RequestRestrictionEdge | null | undefined> | null;
    } | null;
    hasHiddenBlockingRestriction?: boolean | null;
    waitlistRestrictions?: {
      edges?: ReadonlyArray<
        { node?: { id: string } | null } | null | undefined
      > | null;
    } | null;
  };

  const isSlotBlockedByBlockOrEvent = (slotNode: TimeSlotWithRestrictionData) =>
    hideUnavailableTimes
      ? isBlockedByBlockOrEventSlotAvailability(slotNode.availability)
      : slotNode.eventSlot?.type === "EVENT" ||
        slotNode.eventSlot?.type === "BUFFER" ||
        !!slotNode.block;

  const hiddenClosedRequestRestrictionSlotIds = new Set<string>();
  const requestRestrictionSlotGroups = timeSlotEdges.reduce((acc, edge) => {
    const slotNode: TimeSlotWithRestrictionData | null | undefined = edge?.node;
    if (!slotNode?.id || !slotNode?.course?.id) {
      return acc;
    }

    const requestRestrictionEdges = requestConfigEnabled
      ? slotNode.configuredRequestRestrictions?.edges
      : slotNode.requestRestrictions?.edges;

    const requestRestrictions =
      requestRestrictionEdges?.flatMap((restrictionEdge) => {
        const normalizedRule = normalizeBookingRule(restrictionEdge?.node);
        const requestState = requestConfigEnabled
          ? getRequestRestrictionState(restrictionEdge, nowMs)
          : "request_open";

        if (!normalizedRule || requestState !== "request_open") {
          if (
            normalizedRule &&
            requestState === "blocking" &&
            ["NONE", "RESTRICTION_ONLY"].includes(
              normalizedRule.visibility ?? ""
            )
          ) {
            hiddenClosedRequestRestrictionSlotIds.add(slotNode.id);
          }

          return [];
        }

        return [
          {
            ...normalizedRule,
            liftAt: restrictionEdge?.liftAt ?? null,
            requestOpens: restrictionEdge?.requestOpens ?? null,
            requestCloses: restrictionEdge?.requestCloses ?? null,
          },
        ];
      }) ?? [];

    const shouldRenderVisibleBlockInline =
      !!hideUnavailableTimes &&
      isSlotBlockedByBlockOrEvent(slotNode) &&
      !!slotNode.block?.id &&
      !!slotNode.block.blockSeries?.isVisibleToMembers &&
      !inlineBlockIds.has(slotNode.block.id);

    requestRestrictions.forEach((restriction) => {
      if (!restriction?.id) {
        return;
      }

      if (!acc[restriction.id]) {
        acc[restriction.id] = [];
      }

      if (shouldRenderVisibleBlockInline && slotNode.block?.id) {
        const blockProps = buildBlockProps(slotNode.block);

        if (blockProps) {
          acc[restriction.id].push({
            type: "Block",
            id: slotNode.block.id,
            time: slotNode.time,
            block: blockProps,
            restriction,
          });
          inlineBlockIds.add(slotNode.block.id);
          inlineBlockKeys.add(getBlockPropsKey(blockProps));
        }

        return;
      }

      if (hideUnavailableTimes && isSlotBlockedByBlockOrEvent(slotNode)) {
        return;
      }

      acc[restriction.id].push({
        type: "RequestSlot",
        id: slotNode.id,
        time: slotNode.time,
        availability: slotNode.availability,
        course: slotNode.course,
        restriction,
      });
    });

    return acc;
  }, {} as Record<string, RequestRestrictionSlotNode[]>);

  const requestRestrictionSlotIds = new Set<string>(
    Object.values(requestRestrictionSlotGroups)
      .flatMap((restrictionSlots) => restrictionSlots.map((slot) => slot.id))
      .filter(Boolean)
  );

  const blockingRequestRestrictionSlotIds = new Set<string>(
    requestConfigEnabled
      ? timeSlotEdges.flatMap((slotEdge) => {
          const slotNode = slotEdge?.node as
            | TimeSlotWithRestrictionData
            | null
            | undefined;

          if (!slotNode?.id) {
            return [];
          }

          const hasBlockingRequestRestriction =
            slotNode.configuredRequestRestrictions?.edges?.some(
              (restrictionEdge) =>
                getRequestRestrictionState(restrictionEdge, nowMs) ===
                "blocking"
            ) ?? false;

          return hasBlockingRequestRestriction ? [slotNode.id] : [];
        })
      : []
  );

  const requestRestrictedSlotIds = new Set<string>([
    ...requestRestrictionSlotIds,
    ...blockingRequestRestrictionSlotIds,
  ]);

  const groupedRequestRestrictionSlots: GroupedRequestRestrictionRowData[] =
    !isMultiCourse
      ? Object.values(requestRestrictionSlotGroups)
          .filter((restrictionSlots) => restrictionSlots.length > 0)
          .flatMap((restrictionSlots) => {
            const sortedSlots = sortBy(restrictionSlots, "time");
            const restriction = sortedSlots[0].restriction;
            const { startAt, endAt, displayStartAt } = getRestrictionTimes(
              restriction.startAt,
              restriction.endAt
            );

            const groupedRestriction = {
              id: restriction.id,
              name: restriction.name ?? "",
              displayName: restriction.displayName ?? null,
              memberMessage: restriction.memberMessage ?? null,
              startAt,
              endAt,
              displayStartAt,
              allDay: restriction.allDay ?? false,
              liftAt: restriction.liftAt ?? null,
            };

            const slotGroups = sortedSlots.reduce(
              (acc: RequestRestrictionSlotNode[][], slot) => {
                const groupIndex = timedInfoRestrictionStartTimes.reduce(
                  (index, restrictionStartAt) =>
                    compareAgendaTimes(
                      slot.time,
                      restrictionStartAt,
                      firstTimeSlot
                    ) >= 0
                      ? index + 1
                      : index,
                  0
                );

                acc[groupIndex] ??= [];
                acc[groupIndex].push(slot);
                return acc;
              },
              []
            );

            const firstSlotGroupIndex = slotGroups.findIndex(
              (slotGroup) => !!slotGroup?.length
            );

            return slotGroups.flatMap((slotGroup, slotGroupIndex) => {
              if (!slotGroup?.length) {
                return [];
              }

              const groupedSlots: GroupedRequestRowProps[] = slotGroup.map(
                (slot, index) => {
                  if (slot.type === "Block") {
                    return {
                      type: "Block",
                      id: slot.id,
                      time: slot.time,
                      block: slot.block,
                      restriction: groupedRestriction,
                    };
                  }

                  return {
                    type: "RequestSlot",
                    id: queueSlotId ?? "",
                    time: slot.time,
                    availability: slot.availability,
                    course: slot.course,
                    restriction: groupedRestriction,
                    index: index === slotGroup.length - 1 ? -1 : index,
                    onSlotPress: onRequestSlotPress,
                  };
                }
              );

              return [
                {
                  type: "GroupedRequestRestrictionTimeSlot",
                  startAt:
                    slotGroupIndex === firstSlotGroupIndex &&
                    firstSlotGroupIndex === 0
                      ? restriction.startAt || slotGroup[0]?.time || ""
                      : slotGroup[0]?.time || "",
                  data: groupedSlots,
                  showHeader: slotGroupIndex === firstSlotGroupIndex,
                },
              ];
            });
          })
      : [];

  const requestRestrictionsSlots: RequestTimeSlotRowProps[] = isMultiCourse
    ? Object.values(requestRestrictionSlotGroups).reduce(
        (acc: RequestTimeSlotRowProps[], restrictionSlots) => {
          if (!restrictionSlots.length || !queueSlotId) {
            return acc;
          }

          const sortedSlots = sortBy(
            restrictionSlots.filter((slot) => slot.type === "RequestSlot"),
            "time"
          );

          if (!sortedSlots.length) {
            return acc;
          }

          const restriction = sortedSlots[0].restriction;
          const assignedCoursesForRestriction = uniq(
            sortedSlots
              .map((slot) => slot.course.id)
              .filter((courseId): courseId is string => !!courseId)
          );

          const filteredSlots = sortedSlots.filter(
            (slot) => !!slot.course.id && courseIds.includes(slot.course.id)
          );

          const { startAt, endAt, displayStartAt } = getRestrictionTimes(
            restriction.startAt,
            restriction.endAt
          );

          filteredSlots.forEach((slot, index) => {
            const variant =
              assignedCoursesForRestriction.length ===
                data.node?.courses?.count && isMultiCourse
                ? "IS_MULTICOURSE_AND_ASSOCIATED_WITH_ALL_COURSES"
                : isEqual(
                    sortBy(assignedCoursesForRestriction),
                    sortBy(courseIds)
                  ) && isMultiCourse
                ? "IS_MULTICOURSE_AND_ASSOCIATED_WITH_ALL_COURSES"
                : "DEFAULT";

            acc.push({
              variant,
              id: queueSlotId,
              time: slot.time,
              availability: slot.availability,
              course: slot.course,
              restriction: {
                id: restriction.id,
                name: restriction.name ?? "",
                displayName: restriction.displayName ?? null,
                memberMessage: restriction.memberMessage ?? null,
                startAt,
                endAt,
                displayStartAt,
                allDay: restriction.allDay ?? false,
                liftAt: restriction.liftAt ?? null,
              },
              index: index === filteredSlots.length - 1 ? -1 : index,
              onSlotPress: onRequestSlotPress,
            });
          });

          return acc;
        },
        []
      )
    : [];

  const slotVisibleBlocks: TimeSlotListBlockProps[] = hideUnavailableTimes
    ? timeSlotEdges.reduce((acc: TimeSlotListBlockProps[], edge) => {
        const slotNode: TimeSlotWithRestrictionData | null | undefined =
          edge?.node;
        const slotBlock = slotNode?.block;

        if (
          !slotBlock?.id ||
          !slotBlock.blockSeries?.isVisibleToMembers ||
          inlineBlockIds.has(slotBlock.id) ||
          agendaBlockIds.has(slotBlock.id)
        ) {
          return acc;
        }

        const blockProps = buildBlockProps(slotBlock);

        if (
          blockProps &&
          (inlineBlockKeys.has(getBlockPropsKey(blockProps)) ||
            agendaBlockKeys.has(getBlockPropsKey(blockProps)))
        ) {
          return acc;
        }

        if (blockProps) {
          agendaBlockIds.add(slotBlock.id);
          acc.push(blockProps);
        }

        return acc;
      }, [])
    : [];

  const visibleBlocks = blocks
    .filter((block) => !inlineBlockKeys.has(getBlockPropsKey(block)))
    .concat(slotVisibleBlocks);

  // Waitlist Slots
  let lastAvailableSlotTime: string | null = null;
  let consecutiveBlockedSlots = 0;

  const waitlistSlotsForCurrentCourses = sortBy(
    timeSlotEdges.filter((slotEdge) => {
      const slotNode: TimeSlotWithRestrictionData | null | undefined =
        slotEdge?.node;
      return (
        !!slotNode?.waitlistRestrictions?.edges?.length &&
        courseIds.includes(slotNode?.course.id ?? "")
      );
    }),
    (slotEdge) => slotEdge?.node?.dateTime ?? ""
  );

  const waitlistRestrictionSlots: WaitlistCtaProps[] =
    waitlistSlotsForCurrentCourses.length
      ? waitlistSlotsForCurrentCourses.reduce(
          (acc: WaitlistCtaProps[], e, i) => {
            const allSlotsBlocked = waitlistSlotsForCurrentCourses.every(
              (slot) =>
                isBlockedOrUnavailableSlotAvailability(slot?.node?.availability)
            );

            const { time, availability } = e?.node ?? {
              time: "",
            };

            if (isOpenSlotAvailability(availability)) {
              lastAvailableSlotTime = time;
              consecutiveBlockedSlots = 0; // Reset the counter when an available slot is found
            } else {
              consecutiveBlockedSlots++;
            }

            const hoursBetweenSlots =
              time && lastAvailableSlotTime
                ? Math.ceil(
                    differenceInAgendaTimeMinutes(
                      lastAvailableSlotTime,
                      time,
                      firstTimeSlot
                    ) / 60
                  )
                : 0;

            const hasRequestRestriction = !!(
              e?.node?.id && requestRestrictedSlotIds.has(e.node.id)
            );

            const isSlotBlockedOrUnavailable =
              isBlockedOrUnavailableSlotAvailability(e?.node?.availability) ||
              isAccessibleBookingSlotAvailability(e?.node?.availability);

            const hasSignificantBlockedSlotSequence =
              (consecutiveBlockedSlots > 6 || hoursBetweenSlots > 3) &&
              isSlotBlockedOrUnavailable;

            const allSlotsBlockedWithoutCta =
              allSlotsBlocked && acc.length === 0;

            const addedCtaTimes = new Set(acc.map((c) => c.time));
            const hasCtaAtTime = addedCtaTimes.has(time);

            const shouldAddForBlockedSequence =
              hasSignificantBlockedSlotSequence && !hasRequestRestriction;
            const shouldAddForAllBlockedSlots = allSlotsBlockedWithoutCta;
            const shouldAddForInitialBlock = i === 0 && !!e?.node?.block?.id;

            const shouldAddCta =
              ((shouldAddForBlockedSequence || shouldAddForAllBlockedSlots) &&
                !hasCtaAtTime) ||
              shouldAddForInitialBlock;

            if (shouldAddCta) {
              const waitlistCtaRow = {
                time: time ?? "",
                queueSlotId: queueSlotId ?? "",
                clientCanFulfillRequests: clientCanFulfillRequests ?? false,
                supportsPublic: supportsPublic ?? false,
                handleRequestedRowPress: onRequestSlotPress ?? (() => {}),
              };
              acc.push(waitlistCtaRow);
            }

            return acc;
          },
          []
        )
      : [];

  // TimeSlots

  /**
   * If not a private club -- we filter out slots without rates for
   * this user -- to hide slots that are only for specific member types.
   */

  const timeSlotsToMap =
    clubVisibility !== "PRIVATE"
      ? agenda?.timeSlots?.edges?.filter(
          (e) => !!e?.node?.playerRateForMembershipType
        ) ?? []
      : agenda?.timeSlots?.edges ?? [];

  const timeSlots: TimeSlot[] = agenda?.timeSlots?.edges
    ? timeSlotsToMap
        .reduce((acc: TimeSlot[], cur) => {
          const isFilterAll = viewFilter === "All";
          const isFilterAvailable =
            viewFilter === "Available" &&
            (hideUnavailableTimes
              ? isOpenSlotAvailability(cur?.node?.availability) ||
                isAccessibleBookingSlotAvailability(cur?.node?.availability)
              : ["FREE", "AVAILABLE", "ACCESSIBLE"].includes(
                  cur?.node?.availability ?? ""
                ));

          const bookings =
            cur?.node?.bookings?.edges?.map((e) => e?.node) || [];
          const rounds = bookings?.flatMap((b) =>
            b?.rounds?.edges?.map((e) => e?.node)
          );

          const joinableBookings = bookings.filter(
            (b) => b?.writeAccess === "JOIN"
          );

          const permittedCourseLayouts =
            cur?.node?.permittedCourseLayouts?.edges?.map(
              (cl) => cl?.node?.holeCount
            ) || [];

          const timeSlotHasFilteredHoleCounts = !!holeCount
            ? permittedCourseLayouts.includes(holeCount!)
            : true;
          const hasHiddenBlockingRestriction =
            !!cur?.node?.hasHiddenBlockingRestriction;
          const hasHiddenClosedRequestRestriction =
            !!cur?.node?.id &&
            hiddenClosedRequestRestrictionSlotIds.has(cur.node.id);

          const restrictionSlotNode:
            | TimeSlotWithRestrictionData
            | null
            | undefined = cur?.node;

          if (
            cur?.node &&
            restrictionSlotNode &&
            !isSlotBlockedByBlockOrEvent(restrictionSlotNode) &&
            !hasHiddenBlockingRestriction &&
            !hasHiddenClosedRequestRestriction &&
            !requestRestrictionSlotIds.has(cur.node.id) &&
            !blockingRequestRestrictionSlotIds.has(cur.node.id) &&
            timeSlotHasFilteredHoleCounts &&
            (cur.node.capacity - cur.node.usedCapacity >= groupSize ||
              viewFilter === "All") &&
            (isFilterAll || isFilterAvailable)
          ) {
            const newRow: TimeSlot = {
              id: cur.node.id,
              courseName: cur.node.course.name,
              time: cur.node.time,
              data: {
                timeSlotRef: cur.node,
                facilityPermissions: facilityPermissions ?? {
                  memberAccess: "FACILITY_PERMISSIONS_DISABLED",
                  canBookOnBusySlot: false,
                },
                onSlotPress:
                  joinableBookings.length > 0
                    ? onJoinableRowPress
                    : onSlotPress,
                shouldShowCourseName: shouldShowCourseName ?? false,
                isCurrentSlot: cur.node.id === currentSlotId,
                players: rounds
                  ? rounds.map((r) => ({
                      id: r?.player?.id ?? "",
                      firstName: r?.player?.firstName ?? "",
                      lastName: r?.player?.lastName ?? "",
                      type:
                        r?.__typename === "MemberRound" ? "Member" : "Guest",
                    }))
                  : [],
              },
            };
            acc.push(newRow);
          }
          return acc;
        }, [])
        .sort((a, b) => {
          // Only sort if time is the same.
          // Backend sorts time, but not course.
          if (a.time === b.time) {
            const sortedNames = [a.courseName, b.courseName].sort();
            if (a.courseName === sortedNames[0]) {
              return -1;
            } else {
              return 1;
            }
          }
          return 0;
        })
    : [];

  const combinedTimeSlotsAndRestrictions = Object.values(
    [...timeSlots, ...requestRestrictionsSlots].reduce(
      (acc, cur: TimeSlot | RequestTimeSlotRowProps) => {
        if (acc[cur.time]) {
          acc[cur.time].push(cur);
        } else {
          acc[cur.time] = [cur];
        }
        return acc;
      },
      {} as Record<string, Array<TimeSlot | RequestTimeSlotRowProps>>
    )
  );

  const [singles, combined] = partition(
    combinedTimeSlotsAndRestrictions,
    (arr) => arr.length === 1
  );

  const isTimeSlot = (
    val: TimeSlot | RequestTimeSlotRowProps
  ): val is TimeSlot => val.hasOwnProperty("courseName");

  const combinedSorted = combined.map((arr) => {
    return arr.sort((a, b) => {
      const aName = isTimeSlot(a) ? a.courseName : a.course.name || "";
      const bName = isTimeSlot(b) ? b.courseName : b.course.name || "";

      if (aName < bName) {
        return -1;
      } else if (aName > bName) {
        return 1;
      }

      return 0;
    });
  });

  const [_timeSlots, _requestSlots] = singles
    .flatMap((x) => x)
    .reduce(
      (acc, cur) => {
        if (isTimeSlot(cur)) {
          acc[0].push(cur);
        } else {
          acc[1].push(cur);
        }
        return acc;
      },
      [[], []] as [TimeSlot[], RequestTimeSlotRowProps[]]
    );

  // Then need to sort them into order
  const teeSheetDataWithoutWaitlist: TeeSheetAgendaRowData[] = visibleBlocks
    .map(
      (b) =>
        ({
          type: "Block",
          startAt: b.startAt,
          durationMins: b.durationMins,
          data: b,
        } as TeeSheetAgendaRowData)
    )
    .concat(
      events.map(
        (e) =>
          ({
            type: "Event",
            startAt: e.startAt,
            data: e.data,
            key: e.id,
          } as TeeSheetAgendaRowData)
      )
    )
    .concat(
      [...blockingRestrictions, ...closedRequestRestrictions].map((r) => ({
        type: "BlockingRestriction",
        startAt: r.allDay ? "00:00:00" : r.startAt ? r.startAt : "",
        data: r,
      }))
    )
    .concat(
      infoRestrictions
        .filter((r) => {
          // Filter out any restrictions we already have as blocking restrictions
          return blockingRestrictions.findIndex((br) => br.id === r.id) === -1;
        })
        .map((r) => ({
          type: "InfoRestriction",
          startAt: r.allDay ? "00:00:00" : r.startAt ? r.startAt : "",
          endAt: r.allDay ? "23:59:59" : r.endAt ? r.endAt : "",
          data: r,
        }))
    )
    .concat(
      !isMultiCourse
        ? []
        : _requestSlots.map((rs) => ({
            type: "RequestRestrictionTimeSlot",
            startAt: rs.time || "",
            data: rs,
          }))
    )
    .concat(
      groupedRequestRestrictionSlots.map((r) => ({
        type: "GroupedRequestRestrictionTimeSlot",
        startAt: r.startAt,
        data: [...r.data],
        showHeader: r.showHeader,
      }))
    )
    .concat(
      _timeSlots.map((s) => ({
        type: "TimeSlot",
        startAt: s.time,
        data: s.data,
        id: s.id,
      }))
    )
    .concat(
      combinedSorted.map((s) => ({
        type: "CombinedTimeSlotAndRequest",
        startAt: s[0].time,
        data: s.map((x) => (isTimeSlot(x) ? x.data : x)),
        id: `${s[0].id}-${s[1].id}`,
      }))
    )
    .concat(
      weatherConditions.map((w) => ({
        type: "Weather",
        startAt: w.startTime,
        data: w,
      }))
    );

  const countableWaitlistSpacingRowTypes: TeeSheetAgendaRowData["type"][] = [
    "Block",
    "CombinedTimeSlotAndRequest",
    "Event",
    "GroupedRequestRestrictionTimeSlot",
    "RequestRestrictionTimeSlot",
    "TimeSlot",
  ];

  const isCountableWaitlistSpacingRow = (row: TeeSheetAgendaRowData) =>
    countableWaitlistSpacingRowTypes.includes(row.type);

  const waitlistRestrictionSlotsToRender = waitlistRestrictionSlots.reduce(
    (acc: WaitlistCtaProps[], waitlistSlot) => {
      const previousWaitlistCtaTime = acc[acc.length - 1]?.time ?? null;

      if (!previousWaitlistCtaTime) {
        acc.push(waitlistSlot);
        return acc;
      }

      const currentSlotDateTime = waitlistSlot.time
        ? parse(waitlistSlot.time, "HH:mm:ss", new Date())
        : null;
      const previousWaitlistCtaDateTime = parse(
        previousWaitlistCtaTime,
        "HH:mm:ss",
        new Date()
      );
      const hoursSincePreviousWaitlistCta = currentSlotDateTime
        ? differenceInHours(currentSlotDateTime, previousWaitlistCtaDateTime, {
            roundingMethod: "ceil",
          })
        : 0;

      const renderedAgendaRowsSincePreviousCta =
        teeSheetDataWithoutWaitlist.filter(
          (row) =>
            isCountableWaitlistSpacingRow(row) &&
            !!row.startAt &&
            row.startAt > previousWaitlistCtaTime &&
            row.startAt <= waitlistSlot.time
        ).length;

      if (
        hoursSincePreviousWaitlistCta > 3.5 &&
        renderedAgendaRowsSincePreviousCta >=
          MIN_RENDERED_AGENDA_ROWS_BETWEEN_WAITLIST_CTAS
      ) {
        acc.push(waitlistSlot);
      }

      return acc;
    },
    []
  );

  const TeeSheetData: TeeSheetAgendaRowData[] = teeSheetDataWithoutWaitlist
    .concat(
      waitlistRestrictionSlotsToRender.map((w) => ({
        type: "WaitlistCta",
        startAt: w.time,
        data: w,
      }))
    )
    .sort((a, b) => {
      const aStartAt = getAgendaRowSortStartAt(a, firstTimeSlot);
      const bStartAt = getAgendaRowSortStartAt(b, firstTimeSlot);

      if (!aStartAt || !bStartAt) {
        if (!!aStartAt) {
          return 1;
        }
        if (!!bStartAt) {
          return -1;
        }
        return 0;
      }
      return compareAgendaTimes(aStartAt, bStartAt, firstTimeSlot);
    })
    // add this before set weather first, so we get first one the latest on top (if there are two in row.. we get second on top at start)
    .reduce((acc: TeeSheetAgendaRowData[], cur, currentIndex, array) => {
      // delete if two in row
      if (
        acc.length &&
        acc[acc.length - 1].type === "Weather" &&
        cur.type === "Weather"
      ) {
        acc.pop();
        acc.push(cur);
      } else {
        acc.push(cur);
      }

      // delete the last one
      if (cur.type === "Weather" && currentIndex === array.length - 1) {
        acc.pop();
      }
      return acc;
    }, [])
    .reduce((acc: TeeSheetAgendaRowData[], cur) => {
      /**
       * And finally, we always need a weather item first, so that it can appear in the top bar no matter what. For this reason, we just
       * grab the first weather and pull it to the start of the day.
       */
      const isWeatherFirst = acc.length && acc[0].type === "Weather";
      if (!isWeatherFirst && cur.type === "Weather") {
        acc.unshift(cur);
      } else {
        acc.push(cur);
      }
      return acc;
    }, []);

  // Then need to loop through and return components.

  const visibleGroupedRequestRestrictionIds = new Set<string>();

  const TeeSheetComponents: TeeSheetComponent[] = TeeSheetData.map(
    (data, i) => {
      if (data.type === "Block") {
        return {
          key: `block-${data.data.name}${data.startAt}`,
          component: <TimeSlotListBlock {...data.data} />,
          startAt: data.startAt,
        };
      }

      if (data.type === "CombinedTimeSlotAndRequest") {
        return {
          key: `combined-timeslot-${i}-${data.id}`,
          component: (
            <CombinedTimeSlotAndRequestRow
              id={data.id}
              data={data.data}
              clubVisibility={clubVisibility}
            />
          ),
          startAt: data.startAt,
        };
      }

      if (data.type === "Event") {
        return {
          key: `event-${data.key}`,
          component: (
            <Box my={2}>
              <TimeListEventsCard fragmentRef={data.data} />
            </Box>
          ),
          startAt: data.startAt,
        };
      }

      if (data.type === "BlockingRestriction") {
        return {
          key: `restriction-${data.data.name}`,
          component: (
            <RestrictionBlock
              {...data.data}
              serverTimeOffsetMs={serverTimeOffsetMs}
            />
          ),
          startAt: data.startAt,
          isAllDay: data.data.allDay || false,
        };
      }

      if (data.type === "InfoRestriction") {
        return {
          key: `info-restriction-${data.data.name}`,
          component: <RestrictionInfoBar {...data.data} />,
          startAt: data.startAt,
          isAllDay: data.data.allDay || false,
        };
      }

      if (data.type === "RequestRestrictionTimeSlot") {
        return {
          key: `request-restriction-${data.data?.restriction?.name}-${data.data.time}-${data.data.course.id}`,
          startAt: data.data.time,
          component: (
            <RequestTimeSlotRow
              {...data.data}
              clubVisibility={clubVisibility}
            />
          ),
        };
      }
      if (data.type === "GroupedRequestRestrictionTimeSlot") {
        const restrictionId = data.data[0]?.restriction?.id;
        const isVisibleWithinTimeRange =
          !teeSheetTimes ||
          isWithinTeeSheetTimeRange(
            data.data[0]?.time ?? "",
            teeSheetTimes.timeGTE,
            teeSheetTimes.timeLTE
          );
        const isFirstVisibleGroup =
          !!teeSheetTimes &&
          isVisibleWithinTimeRange &&
          !!restrictionId &&
          !visibleGroupedRequestRestrictionIds.has(restrictionId);

        if (isFirstVisibleGroup && restrictionId) {
          visibleGroupedRequestRestrictionIds.add(restrictionId);
        }

        return {
          key: `grouped-request-restriction-${data.data[0]?.restriction?.name}-${data.data[0].time}`,
          startAt: data.data[0].time,
          component: (
            <GroupedRequestTimeSlotRow
              requestRestrictionsSlots={data.data}
              showHeader={data.showHeader || isFirstVisibleGroup}
            />
          ),
        };
      }
      if (data.type === "TimeSlot") {
        return {
          key: `timeslot-${data.id}`,
          component: (
            <Box my={1}>
              <TimeSlotRow {...data.data} clubVisibility={clubVisibility} />
            </Box>
          ),
          startAt: data.startAt,
        };
      }

      if (data.type === "Weather" && i !== 0 && data.startAt) {
        ///Check if next slot is request
        const isInRequest =
          TeeSheetData[i + 1]?.type === "RequestRestrictionTimeSlot";

        return {
          key: `weather-${data.data.id}`,
          component: (
            <TimeSlotListWeatherRow
              weatherCondition={data.data}
              isInRequest={isInRequest}
            />
          ),
          startAt: data.startAt,
        };
      }

      if (data.type === "Weather" && i === 0 && data.startAt) {
        // We do not return the first weather component because it appears in the
        // top bar
        return {
          key: `weather-${data.data.id}`,
          component: <></>,
          startAt: data.startAt,
        };
      }

      if (data.type === "WaitlistCta") {
        return {
          key: `waitlist-cta-${data.data.time}`,
          component: (
            <WaitlistCta
              queueSlotId={data.data.queueSlotId ?? ""}
              time={data.data.time}
              clientCanFulfillRequests={clientCanFulfillRequests ?? false}
              handleRequestedRowPress={onRequestSlotPress}
            />
          ),
          startAt: data.startAt,
        };
      }

      return {
        key: `error-${i}`,
        component: <></>,
        startAt: data.startAt ?? "",
      };
    }
  );

  // Filter components based on time range if teeSheetTimes is provided
  const filteredTeeSheetComponentsBasedOnTime = teeSheetTimes
    ? TeeSheetComponents.filter((component) => {
        if (component.isAllDay) {
          return true;
        }

        return isWithinTeeSheetTimeRange(
          component.startAt,
          teeSheetTimes.timeGTE,
          teeSheetTimes.timeLTE
        );
      })
    : TeeSheetComponents;

  return [filteredTeeSheetComponentsBasedOnTime, TeeSheetData] as const;
};

type TeeSheetAgendaRowData =
  | {
      type: "CombinedTimeSlotAndRequest";
      data: Array<TimeSlotRowProps | RequestTimeSlotRowProps>;
      startAt: string;
      id: string;
    }
  | { type: "Block"; data: TimeSlotListBlockProps; startAt: string }
  | {
      type: "Event";
      data: TimeListEventsCard_EventSession$key;
      startAt: string;
      key: string;
    }
  | {
      type: "BlockingRestriction";
      data: RestrictionBlockProps;
      startAt: string;
    }
  | {
      type: "InfoRestriction";
      data: RestrictionInfoBarProps;
      startAt: string;
    }
  | { type: "TimeSlot"; data: TimeSlotRowProps; startAt: string; id: string }
  | {
      type: "Weather";
      data: WeatherConditionProps;
      startAt: string | undefined;
    }
  | {
      type: "RequestRestrictionTimeSlot";
      data: RequestTimeSlotRowProps;
      startAt: string;
    }
  | {
      type: "GroupedRequestRestrictionTimeSlot";
      data: GroupedRequestRowProps[];
      startAt: string;
      showHeader?: boolean;
    }
  | {
      type: "WaitlistCta";
      data: WaitlistCtaProps;
      startAt: string;
    };

type GroupedRequestRestrictionRowData = Extract<
  TeeSheetAgendaRowData,
  { type: "GroupedRequestRestrictionTimeSlot" }
>;

type TeeSheetComponent = {
  key: string;
  component: React.JSX.Element;
  startAt: string;
  isAllDay?: boolean;
};
